import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";
import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { createUuid } from "../../../lib/trace/context";
import {
  fetchCustomerQuoteBudgetReturnContext,
  returnCustomerQuoteBudget,
  retryCustomerQuoteBudgetReturn,
} from "../repo/budgetReturnRepo";
import type {
  CustomerQuoteBudgetReturnCommand,
  CustomerQuoteBudgetReturnResult,
} from "../types/budgetReturn";

type Options = {
  customerQuoteId: string;
  prepare: () => Promise<{ quoteRevision: string; sourceRevision: string | null }>;
  onCompleted: () => Promise<unknown>;
  onBusyChange: (busy: boolean) => void;
};

type RequestScope = {
  active: boolean;
  inFlight: boolean;
  requestId: string | null;
  command: CustomerQuoteBudgetReturnCommand | null;
};

/** 退回状态独立于报价保存；失败保留输入和命令，切换报价使旧请求失效。 */
export function useCustomerQuoteBudgetReturn({ customerQuoteId, prepare, onCompleted, onBusyChange }: Options) {
  const [open, setOpen] = useState(false);
  const [reason, updateReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CustomerQuoteBudgetReturnResult | null>(null);
  const scopeRef = useRef<RequestScope | null>(null);
  useLayoutEffect(() => {
    const scope: RequestScope = { active: true, inFlight: false, requestId: null, command: null };
    scopeRef.current = scope;
    setOpen(false);
    updateReason("");
    setBusy(false);
    setError(null);
    setResult(null);
    return () => { scope.active = false; };
  }, [customerQuoteId]);

  const context = useQuery({
    queryKey: ["customerQuoteBudgetReturn", customerQuoteId],
    queryFn: () => fetchCustomerQuoteBudgetReturnContext(customerQuoteId),
    enabled: open, retry: false, refetchOnWindowFocus: false,
  });
  const quoteMissing = isHttpClientError(context.error) && context.error.code === "CUSTOMER_QUOTE_NOT_FOUND";
  const contextError = !context.isError ? null : quoteMissing
    ? "当前报价已不存在，请返回原核价重新进入报价。当前页面可能保留了已清理的旧报价。"
    : isHttpClientError(context.error) && context.error.code === "CUSTOMER_QUOTE_SCHEMA_NOT_READY"
      ? "退回功能所需的数据结构尚未发布，请联系管理员完成发布后重试。"
      : resolveReadableHttpErrorMessage(context.error, "退回信息加载失败，请稍后重试。");
  const latest = context.data?.latestReturn ?? null;
  const visibleResult = result?.returnId === latest?.returnId ? latest : result ?? latest;

  /** 串行执行本轮退回或补发，并隔离过期页面的回调。 */
  async function perform(retry: boolean) {
    const scope = scopeRef.current;
    if (!scope?.active || scope.inFlight) return;
    scope.inFlight = true;
    setBusy(true);
    onBusyChange(true);
    setError(null);
    try {
      if (contextError) throw new Error(contextError);
      let response: CustomerQuoteBudgetReturnResult;
      if (retry) {
        if (!visibleResult?.canRetry) throw new Error("当前没有可补发的通知");
        response = await retryCustomerQuoteBudgetReturn(customerQuoteId, visibleResult.returnId);
      } else {
        if (!scope.command && !context.data?.canReturn) throw new Error(context.data?.blockedReason ?? "当前核价不能取消定稿");
        scope.requestId ??= createUuid();
        const revision = await prepare();
        if (!scope.active) return;
        if (scope.command && scope.command.expectedQuoteRevision !== revision.quoteRevision) {
          throw new Error("报价编辑基线已变化，请先核对上次退回结果；当前原因和请求已保留");
        }
        if (!scope.command) {
          // 报价使用编辑/保存基线；仅来源核价允许通过本次查询更新。
          const fresh = await context.refetch({ throwOnError: true });
          if (!scope.active) return;
          if (!fresh.data?.canReturn) throw new Error(fresh.data?.blockedReason ?? "当前核价不能取消定稿");
          if (!fresh.data.sourceRevision) throw new Error("核价修订不可用，请重新检查");
          scope.command = {
            requestId: scope.requestId, expectedQuoteRevision: revision.quoteRevision,
            expectedSourceRevision: fresh.data.sourceRevision, reason,
          };
        }
        // 请求结果未知或冲突时保持原命令，不能偷换 revision 或复用幂等键发送其他内容。
        response = await returnCustomerQuoteBudget(customerQuoteId, scope.command);
      }
      if (!scope.active) return;
      setResult(response);
      if (!retry) {
        scope.command = null;
        scope.requestId = null;
        updateReason("");
      }
      // 通知补发不修改报价，不能刷新并覆盖用户后来输入的草稿。
      if (!retry) await onCompleted();
      if (scope.active) await context.refetch();
    } catch (cause) {
      if (scope.active) setError(cause instanceof Error ? cause.message : "操作失败，当前输入已保留，请重试");
    } finally {
      scope.inFlight = false;
      if (scope.active) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  }

  return {
    open, reason, busy, error, context, contextError, quoteMissing, result: visibleResult,
    setReason: (value: string) => {
      const scope = scopeRef.current;
      if (!scope?.active || scope.inFlight) return;
      if (value !== reason) { scope.requestId = null; scope.command = null; }
      updateReason(value);
    },
    show: () => { if (!scopeRef.current?.inFlight) { setOpen(true); setError(null); } },
    close: () => { if (!scopeRef.current?.inFlight) setOpen(false); },
    submit: () => perform(false), retry: () => perform(true),
  };
}
